import instance, { ApiResponse } from 'api';
import {
  getDetails,
  LessonsApi,
  Subject,
  SubjectContents,
} from 'api/lessonsApi';
import { endpoints } from 'endpoints.config';

export type SubjectSiteFooterLink = {
  name: string;
  targetURL: string;
  url: string;
};

export type LessonMenuItem = {
  activity: {
    name: string;
    strippedName: string;
    colorCSS: string | null;
    bIsGroup: boolean;
    orderNumber: number;
  };
  years: string;
  yearName: string;
};

export type LessonMenu = {
  lessonPlanOrder: 'KeySyage_Activity_Year' | 'KeySyage_Year_Activity' | null;
  keyStages: { name: string; strippedName: string; items: LessonMenuItem[] }[];
};

/**
 * What a subject's header, menus and footer show on every page under its path
 */
export type SubjectSiteShell = {
  subject: Subject;
  footerLinks: SubjectSiteFooterLink[];
  cupboardTitle: string | null;
  contents: SubjectContents;
};

/**
 * The signed-in school as the subject site's header shows it
 */
export type SubjectSiteAccount = {
  schoolName: string;
  logoUrl: string | null;
  bMainAccountHolder: boolean;
  bFreeTrial: boolean;
  subscriptionRenewalDate: string | null;
  /**
   * A paid subscription to the subject that has not reached its renewal date
   */
  bPaidSubscription: boolean;
  /**
   * The subject site's auto-login address, as the dashboard links to it
   */
  subjectSiteLink: string | null;
};

type DashboardSubject = {
  subject: string;
  link: string | null;
  renewalDate: string | null;
  isFreeTrial: boolean | null;
  bMarkSubscriptionAsPaid: boolean | null;
};

type Dashboard = {
  listSubjects: DashboardSubject[] | null;
  isMainAccountHolder: boolean | null;
};

type School = {
  schoolName: string;
  logoUrl: string | null;
};

const SHELL_CACHE_MILLISECONDS = 5 * 60 * 1000;

const cachedShells: {
  [code: string]: { shell: SubjectSiteShell; expires: number };
} = {};

/**
 * Gets a part of the shell, or null when it is not found or fails, so the
 * rest still shows
 */
const getShellPart = async <T>(url: string, params: object) => {
  const result = await getDetails<T>(url, params);
  return {
    details: typeof result === 'string' ? null : result,
    bFailed: result === 'error',
  };
};

export class SubjectSiteApi {
  /**
   * Gets the header and footer for a page under a subject's path, cached for
   * 5 minutes once every part has loaded, or null when the subject is not found
   * @param code - The subject code from the path, for example "pe"
   */
  public static getShell = async (
    code: string
  ): Promise<SubjectSiteShell | null> => {
    const key = code.toLowerCase();
    const cached = cachedShells[key];
    if (cached && cached.expires > Date.now()) return cached.shell;

    const subject = await LessonsApi.getSubjectByCode(code);
    if (typeof subject === 'string') return null;

    const params = { subjectsId: subject.subjectsID };
    const [footer, cupboard, contents] = await Promise.all([
      getShellPart<{ links: SubjectSiteFooterLink[] }>(
        endpoints.subjectSite.footerLinks,
        params
      ),
      getShellPart<{ title: string }>(endpoints.subjectSite.cupboard, params),
      LessonsApi.getSubjectContents(subject.subjectsID),
    ]);

    const shell: SubjectSiteShell = {
      subject,
      footerLinks: footer.details?.links ?? [],
      cupboardTitle: cupboard.details?.title ?? null,
      contents,
    };

    if (!footer.bFailed && !cupboard.bFailed && Object.keys(contents).length) {
      cachedShells[key] = {
        shell,
        expires: Date.now() + SHELL_CACHE_MILLISECONDS,
      };
    }

    return shell;
  };

  /**
   * Gets the signed-in school's name, logo and its subscription to the subject,
   * or null when they can't be loaded
   */
  public static getAccount = async (
    subject: Subject
  ): Promise<SubjectSiteAccount | null> => {
    try {
      const [schoolResponse, dashboardResponse] = await Promise.all([
        instance.get<ApiResponse<School>>(
          endpoints.manageschool.getSchoolDetails
        ),
        instance.get<ApiResponse<Dashboard>>(endpoints.dashboard.list),
      ]);
      const school = schoolResponse.data.details;
      const dashboard = dashboardResponse.data.details;
      const subscription = dashboard.listSubjects?.find(
        (item) => item.subject === subject.name
      );
      const renewalDate = subscription?.renewalDate ?? null;

      return {
        schoolName: school.schoolName,
        logoUrl: school.logoUrl || null,
        bMainAccountHolder: !!dashboard.isMainAccountHolder,
        bFreeTrial: !!subscription?.isFreeTrial,
        subscriptionRenewalDate: renewalDate,
        bPaidSubscription:
          !!subscription?.bMarkSubscriptionAsPaid &&
          !!renewalDate &&
          new Date() <= new Date(renewalDate),
        subjectSiteLink: subscription?.link || null,
      };
    } catch {
      return null;
    }
  };

  public static getLessonMenu = (subjectsId: number) =>
    getDetails<LessonMenu>(endpoints.subjectSite.lessonMenu, { subjectsId });
}
